import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription, type SubscriptionTier } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';

interface TierLimits {
  strategies_deployed: number;
  signals_approved: number;
  reports_generated: number;
  csv_exports: number;
  broker_connections_live: number;
  auto_execute: boolean;
  live_trading: boolean;
}

const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    strategies_deployed: 2,
    signals_approved: 5,
    reports_generated: 3,
    csv_exports: 5,
    broker_connections_live: 0,
    auto_execute: false,
    live_trading: false,
  },
  pro: {
    strategies_deployed: 10,
    signals_approved: 999,
    reports_generated: 999,
    csv_exports: 999,
    broker_connections_live: 3,
    auto_execute: false,
    live_trading: true,
  },
  quant: {
    strategies_deployed: 999,
    signals_approved: 999,
    reports_generated: 999,
    csv_exports: 999,
    broker_connections_live: 999,
    auto_execute: true,
    live_trading: true,
  },
};

type Metric = keyof TierLimits;

export interface LimitCheck {
  allowed: boolean;
  used: number;
  limit: number;
  message: string | null;
}

export function useUsageLimits() {
  const { user } = useAuth();
  const { tier, isLoading: tierLoading } = useSubscription();

  const currentPeriod = new Date().toISOString().slice(0, 7); // YYYY-MM

  const { data: usageData = [], isLoading: usageLoading } = useQuery({
    queryKey: ['usage-tracking', user?.id, currentPeriod],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('usage_tracking')
        .select('metric, count')
        .eq('user_id', user.id)
        .eq('period', currentPeriod);
      if (error) {
        console.error('Failed to fetch usage:', error);
        return [];
      }
      return data || [];
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  const limits = TIER_LIMITS[tier];

  const getUsed = (metric: string): number => {
    const entry = usageData.find((u) => u.metric === metric);
    return entry?.count ?? 0;
  };

  const checkLimit = (metric: Metric): LimitCheck => {
    const limitVal = limits[metric];
    if (typeof limitVal === 'boolean') {
      return {
        allowed: limitVal,
        used: 0,
        limit: 0,
        message: limitVal ? null : `${metric.replace(/_/g, ' ')} is available on a higher tier`,
      };
    }
    const used = getUsed(metric);
    const allowed = used < limitVal;
    return {
      allowed,
      used,
      limit: limitVal,
      message: allowed ? null : `You've used ${used} of ${limitVal} ${metric.replace(/_/g, ' ')} this month`,
    };
  };

  const canDeploy = checkLimit('strategies_deployed').allowed;
  const canApproveSignal = checkLimit('signals_approved').allowed;
  const canExportCSV = checkLimit('csv_exports').allowed;
  const canGenerateReport = checkLimit('reports_generated').allowed;
  const canLiveTrading = limits.live_trading;
  const canAutoExecute = limits.auto_execute;

  return {
    tier,
    limits,
    canDeploy,
    canApproveSignal,
    canExportCSV,
    canGenerateReport,
    canLiveTrading,
    canAutoExecute,
    usageData,
    checkLimit,
    isLoading: tierLoading || usageLoading,
  };
}
